import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToolbarButton } from '@fluentui/react-components';
import { useAppDispatch, useSelector } from '../../../../store/hooks';
import { RootState } from '../../../../store';
import ConfirmDeletionDialog from '../../components/ConfirmDeletionDialog/ConfirmDeletionDialog';
import CreateOrUpdateCategoryDialogAction, {
    CategoryDialogMode,
} from './components/CreateOrUpdateCategoryDialogAction';
import { Add24Regular } from '@fluentui/react-icons';
import {
    loadCategories,
    updateCategories,
    createCategories,
    deleteCategories,
    searchCategories,
} from '../../../../store/features/categories/categoriesSlice';
import { selectAppConfigurationSearchQuery } from '../../../../store/features/appConfiguration/appConfigurationSlice';
import ConfigurationTable from '../../components/ConfigurationTable/ConfigurationTable';
import {
    IConfigRow,
    RowAction,
    useDefaultColumns,
} from '../../components/ConfigurationTable/ConfigurationTable.utils';

const CategoriesConfigPanel: React.FC = () => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const { items, loading, pagination } = useSelector(selectCategoriesPanel);
    const searchQuery = useSelector(selectAppConfigurationSearchQuery);

    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [dialogMode, setDialogMode] = useState<CategoryDialogMode>();
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [selectedCategoryKey, setSelectedCategoryKey] = useState<string>('');
    const [deletionTarget, setDeletionTarget] = useState<IConfigRow | null>(null);

    const resetDialogState = useCallback((open: boolean) => {
        setDialogOpen(open);
        setDialogMode(undefined);
        setSelectedCategory('');
        setSelectedCategoryKey('');
    }, []);

    const onItemEditCallback: RowAction<IConfigRow> = useCallback(
        (item) => {
            const category = items.find((c) => c.title === item.value);
            if (category) {
                setDialogMode(CategoryDialogMode.Edit);
                setSelectedCategory(category.title);
                setSelectedCategoryKey(category.rowKey);
                setDialogOpen(true);
            }
        },
        [items]
    );

    const onDeleteCallback: RowAction<IConfigRow> = useCallback(
        (item) => {
            setDeletionTarget(item);
        },
        []
    );

    const closeDeletionDialog = useCallback(() => {
        setDeletionTarget(null);
    }, []);

    const deletionMessage = useMemo(
        () =>
            deletionTarget
                ? t('ConfirmCategoryDeletionDialog_Content').replace(
                      '{category}',
                      deletionTarget.value
                  )
                : '',
        [deletionTarget, t]
    );

    useEffect(() => {
        dispatch(loadCategories());
    }, [dispatch]);

    useEffect(() => {
        dispatch(searchCategories(searchQuery));
    }, [dispatch, searchQuery]);

    const configRows: IConfigRow[] = useMemo(
        () =>
            items.map((item) => ({
                id: item.rowKey,
                value: item.title,
            })),
        [items]
    );

    const columns = useDefaultColumns<IConfigRow>({
        valueHeader: t('TableHeader_Categories'),
        onEdit: onItemEditCallback,
        onDelete: onDeleteCallback,
    });

    return (
        <>
            <ConfigurationTable<IConfigRow>
                items={configRows}
                columns={columns}
                ariaLabel="categoriesTable"
                pagination={{
                    currentPage: pagination?.currentPage ?? 1,
                    pageSize: pagination?.pageSize ?? 10,
                    totalCount: pagination?.totalCount ?? items.length,
                }}
                bottomActions={
                    <ToolbarButton
                        onClick={() => {
                            setDialogMode(CategoryDialogMode.New);
                            setDialogOpen(true);
                        }}
                        icon={<Add24Regular />}
                    >
                        {t('ToolbarButton_AddCategory')}
                    </ToolbarButton>
                }
                loadStatus={loading === 'loading' ? 'loading' : 'idle'}
            />

            <CreateOrUpdateCategoryDialogAction
                open={dialogOpen}
                mode={dialogMode ?? CategoryDialogMode.New}
                existingCategoryTitles={items.map((item) => item.title)}
                categoryTitle={selectedCategory}
                categoryKey={selectedCategoryKey}
                onOpenChange={(_, data) => {
                    resetDialogState(data.open);
                }}
                onSaveBtnClick={(
                    e,
                    {
                        categoryKey: selectedCategoryKey,
                        newCategoryTitle: newCategory,
                    }
                ) => {
                    resetDialogState(false);
                    if (dialogMode === CategoryDialogMode.Edit) {
                        dispatch(
                            updateCategories({
                                rowKey: selectedCategoryKey,
                                title: newCategory,
                            })
                        );
                    } else {
                        dispatch(
                            createCategories({
                                rowKey: newCategory,
                                title: newCategory,
                            })
                        );
                    }
                }}
            />

            <ConfirmDeletionDialog
                open={!!deletionTarget}
                message={deletionMessage}
                onConfirm={() => {
                    if (!deletionTarget) {
                        return;
                    }

                    const category = items.find(
                        (c) => c.title === deletionTarget.value
                    );

                    if (category) {
                        dispatch(deleteCategories(category.rowKey));
                    }

                    closeDeletionDialog();
                }}
                onClose={closeDeletionDialog}
            />
        </>
    );
};

export const selectCategoriesPanel = (state: RootState) =>
    state.categoriesPanel;

export default CategoriesConfigPanel;
